import { LucideIcon, CreditCard, TrendingUp, Calendar, AlertCircle, Users, DollarSign, FileText, Zap } from 'lucide-react';

export interface CreditAction {
  id: string;
  name: string;
  description: string;
  estimatedImpact: number;
  timeToImpact: number;
  difficulty: 'easy' | 'moderate' | 'hard';
  costEstimate?: string;
  requirements: string[];
  icon: LucideIcon;
}

export interface CreditScoreProjection {
  currentScore: number;
  targetScore: number;
  monthlyProjection: MonthlyScore[];
  actionsSelected: CreditAction[];
  totalPointsGained: number;
  monthsToTarget: number;
  interestRateSavings: number;
  lifetimeSavings: number;
  confidenceLevel: 'conservative' | 'realistic' | 'optimistic';
}

export interface MonthlyScore {
  month: number;
  score: number;
  actions: string[];
  confidenceRange: { low: number; high: number };
}

export const creditActions: CreditAction[] = [
  {
    id: 'pay-down-credit-cards',
    name: 'Pay Down Credit Card Balances',
    description: 'Reduce credit utilization from current level to under 30%',
    estimatedImpact: 30,
    timeToImpact: 1,
    difficulty: 'moderate',
    requirements: ['Have funds to pay down balances', 'Wait for statement close'],
    icon: CreditCard
  },
  {
    id: 'reduce-utilization-10',
    name: 'Reduce Utilization to Under 10%',
    description: 'Optimal utilization rate for maximum score boost',
    estimatedImpact: 40,
    timeToImpact: 1,
    difficulty: 'hard',
    costEstimate: 'Varies',
    requirements: ['Significant funds to pay down', 'Disciplined spending'],
    icon: TrendingUp
  },
  {
    id: 'on-time-payments-6mo',
    name: '6 Months Perfect Payment History',
    description: 'Make all payments on time for 6 consecutive months',
    estimatedImpact: 20,
    timeToImpact: 6,
    difficulty: 'easy',
    requirements: ['Set up autopay', 'Maintain sufficient funds'],
    icon: Calendar
  },
  {
    id: 'dispute-errors',
    name: 'Dispute Credit Report Errors',
    description: 'Remove incorrect negative items from credit report',
    estimatedImpact: 30,
    timeToImpact: 3,
    difficulty: 'easy',
    requirements: ['Review credit reports', 'File disputes with bureaus'],
    icon: AlertCircle
  },
  {
    id: 'become-authorized-user',
    name: 'Become Authorized User',
    description: "Added to family member's card with good history",
    estimatedImpact: 22,
    timeToImpact: 2,
    difficulty: 'easy',
    requirements: ['Find willing family member', 'Must be account in good standing'],
    icon: Users
  },
  {
    id: 'credit-builder-loan',
    name: 'Credit Builder Loan',
    description: 'Take small installment loan to build credit mix',
    estimatedImpact: 15,
    timeToImpact: 6,
    difficulty: 'easy',
    costEstimate: '$500-$1000',
    requirements: ['Small upfront payment', '6-12 month commitment'],
    icon: DollarSign
  },
  {
    id: 'pay-collections',
    name: 'Pay Off Collections',
    description: 'Settle and remove collection accounts',
    estimatedImpact: 30,
    timeToImpact: 4,
    difficulty: 'moderate',
    costEstimate: 'Amount owed',
    requirements: ['Negotiate pay-for-delete', 'Get agreement in writing'],
    icon: FileText
  },
  {
    id: 'experian-boost',
    name: 'Use Experian Boost',
    description: 'Add utility and phone payments to credit file',
    estimatedImpact: 10,
    timeToImpact: 0,
    difficulty: 'easy',
    requirements: ['Link bank account', 'Have positive payment history'],
    icon: Zap
  }
];

export const simulateCreditScoreImprovement = (
  currentScore: number,
  selectedActions: CreditAction[],
  targetMonths: number = 12,
  confidenceLevel: 'conservative' | 'realistic' | 'optimistic' = 'realistic'
): CreditScoreProjection => {
  const monthlyProjection: MonthlyScore[] = [];
  let score = currentScore;
  
  const multipliers = {
    conservative: 0.6,
    realistic: 0.8,
    optimistic: 1.0
  };
  const multiplier = multipliers[confidenceLevel];
  
  for (let month = 0; month <= targetMonths; month++) {
    const activeActions: string[] = [];
    let monthlyGain = 0;
    
    selectedActions.forEach(action => {
      if (month >= action.timeToImpact) {
        const impactPerMonth = (action.estimatedImpact * multiplier) / Math.max(action.timeToImpact, 1);
        const decayFactor = 1 - ((month - action.timeToImpact) * 0.05);
        monthlyGain += impactPerMonth * Math.max(0.5, decayFactor);
        
        if (month === action.timeToImpact || month === action.timeToImpact + 1) {
          activeActions.push(action.name);
        }
      }
    });
    
    score += monthlyGain;
    score = Math.min(850, score);
    
    monthlyProjection.push({
      month,
      score: Math.round(score),
      actions: activeActions,
      confidenceRange: {
        low: Math.round(Math.max(300, score - 15)),
        high: Math.round(Math.min(850, score + 15))
      }
    });
  }
  
  const finalScore = monthlyProjection[targetMonths].score;
  const totalPointsGained = finalScore - currentScore;
  
  const currentRate = getCreditScoreTier(currentScore).rate;
  const improvedRate = getCreditScoreTier(finalScore).rate;
  const rateSavings = currentRate - improvedRate;
  
  const loanAmount = 320000;
  const loanTermYears = 30;
  const currentMonthlyPayment = calculateMortgagePayment(loanAmount, currentRate, loanTermYears);
  const improvedMonthlyPayment = calculateMortgagePayment(loanAmount, improvedRate, loanTermYears);
  const monthlySavings = currentMonthlyPayment - improvedMonthlyPayment;
  const lifetimeSavings = monthlySavings * loanTermYears * 12;
  
  return {
    currentScore,
    targetScore: finalScore,
    monthlyProjection,
    actionsSelected: selectedActions,
    totalPointsGained,
    monthsToTarget: targetMonths,
    interestRateSavings: rateSavings,
    lifetimeSavings,
    confidenceLevel
  };
};

export const getCreditScoreTier = (score: number): { tier: string; rate: number } => {
  if (score >= 780) return { tier: 'Exceptional', rate: 6.0 };
  if (score >= 740) return { tier: 'Very Good', rate: 6.25 };
  if (score >= 700) return { tier: 'Good', rate: 6.5 };
  if (score >= 680) return { tier: 'Fair', rate: 6.75 };
  if (score >= 640) return { tier: 'Poor', rate: 7.0 };
  if (score >= 580) return { tier: 'Very Poor', rate: 7.5 };
  return { tier: 'Bad', rate: 8.0 };
};

const calculateMortgagePayment = (principal: number, annualRate: number, years: number): number => {
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = years * 12;
  
  if (monthlyRate === 0) return principal / numberOfPayments;
  
  return principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
};
